import { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useChat } from "../hooks/useChat";
import { useViewportHeight } from "../hooks/useViewportHeight";
import { useWsFallbackPolling } from "../hooks/useWsFallbackPolling";
import { Sidebar } from "../components/Sidebar";
import { ChatView } from "../components/ChatView";
import { SettingsPage } from "./SettingsPage";
import { AdminPage } from "./AdminPage";
// Logo image used instead of MessageSquare icon
import styles from "./ChatPage.module.css";

export function ChatPage() {
  const { user, logout, e2eeReady } = useAuth();
  const { loadChannels, activeChannelId, initWebSocketHandlers, setActiveChannel } = useChat();
  const [showSettings, setShowSettings] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  useViewportHeight();
  // HTTP-Polling wenn WS geblockt (Firmen-Proxy) — KOL-27
  useWsFallbackPolling();

  useEffect(() => {
    // Wait for E2EE to be fully ready (keys + backup restore) before loading channels/messages
    if (!e2eeReady) return;
    loadChannels();
    const cleanup = initWebSocketHandlers();
    return cleanup;
  }, [loadChannels, initWebSocketHandlers, e2eeReady]);

  // Handle push notification clicks — navigate to the right channel
  useEffect(() => {
    const handlePushNavigate = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.channelId) {
        setActiveChannel(detail.channelId);
        setShowSettings(false);
      }
    };

    window.addEventListener("push-navigate", handlePushNavigate);
    return () => window.removeEventListener("push-navigate", handlePushNavigate);
  }, [setActiveChannel]);

  // Close settings/admin/support when a chat is selected from the sidebar
  useEffect(() => {
    if (activeChannelId) {
      setShowSettings(false);
      setShowAdmin(false);
    }
  }, [activeChannelId]);

  if (!user) return null;

  const canAccessAdmin = user.orgRole === "owner" || user.orgRole === "admin";

  // Show loading while E2EE initializes (keys + backup restore)
  if (!e2eeReady) {
    return (
      <div className={styles.layout}>
        <div className={styles.empty} style={{ gridColumn: "1 / -1" }}>
          <div className={styles.emptyContent}>
            <img src="/logo.png" alt="KollegePlus" className={styles.emptyLogo} />
            <p>Nachrichten werden vorbereitet…</p>
          </div>
        </div>
      </div>
    );
  }

  if (showAdmin && canAccessAdmin) {
    return (
      <div className={`${styles.layout} ${styles.chatActive}`}>
        <Sidebar user={user} onLogout={logout} onSettings={() => { setShowAdmin(false); setShowSettings(true); }} onAdmin={canAccessAdmin ? () => { setShowSettings(false); setShowAdmin(true); } : undefined} isAdmin={canAccessAdmin} />
        <main className={styles.main}>
          <AdminPage onBack={() => setShowAdmin(false)} />
        </main>
      </div>
    );
  }

  if (showSettings) {
    return (
      <div className={`${styles.layout} ${styles.chatActive}`}>
        <Sidebar user={user} onLogout={logout} onSettings={() => setShowSettings(true)} onAdmin={canAccessAdmin ? () => { setShowSettings(false); setShowAdmin(true); } : undefined} isAdmin={canAccessAdmin} />
        <main className={styles.main}>
          <SettingsPage onBack={() => setShowSettings(false)} onNavigateToAdmin={canAccessAdmin ? () => { setShowSettings(false); setShowAdmin(true); } : undefined} />
        </main>
      </div>
    );
  }

  return (
    <div className={`${styles.layout} ${activeChannelId ? styles.chatActive : ""}`}>
      <Sidebar user={user} onLogout={logout} onSettings={() => setShowSettings(true)} onAdmin={canAccessAdmin ? () => { setShowSettings(false); setShowAdmin(true); } : undefined} isAdmin={canAccessAdmin} />
      <main className={styles.main}>
        {activeChannelId ? (
          <ChatView
            channelId={activeChannelId}
            currentUserId={user.id}
            onBack={() => setActiveChannel("")}
          />
        ) : (
          <div className={styles.empty}>
            <div className={styles.emptyContent}>
              <img src="/logo.png" alt="KollegePlus" className={styles.emptyLogo} />
              <h2>Willkommen bei KollegePlus</h2>
              <p>Wähle einen Chat oder starte ein neues Gespräch.</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
